import { BehaviorSubject, Observable, take, filter } from 'rxjs';

/**
 * @module FaceService
 */
function createFaceService(
    apiService,
    logService,
    util //,
    // _window
) {
    const _faceService = {};
    const faceSubjects = {};
    const faceIconSubjects = {};
    const faceSubscriptionsForIcon = {};
    const all = 'all';
    const none = 'none';
    const faceNone = 'faceNone';
    const faceAdd = 'faceAdd';
    const faceUnknown = 'faceUnknown';

    let getIdentitiesInProgress;

    function log(params) {
        params.module = 'faceService';
        logService.log(params);
        return params;
    }

    function clear() {
        Object.keys(faceIconSubjects).forEach(key => faceIconSubjects[key].next());
        clearFaceSubjects();
        Object.keys(faceSubjects).forEach(key => delete faceSubjects[key]);
        Object.keys(faceSubscriptionsForIcon).forEach(key => {
            faceSubscriptionsForIcon[key]?.unsubscribe();
            delete faceSubscriptionsForIcon[key];
        });
    }

    function faceSort(a, b) {
        if (a.id === none) {
            return 1;
        }
        if (b.id === none) {
            return -1;
        }
        if (a.id.startsWith('-')) {
            if (b.id.startsWith('-')) {
                return a.label.localeCompare(b.label);
            }
            return 1;
        }
        if (b.id.startsWith('-')) {
            if (a.id.startsWith('-')) {
                return a.label.localeCompare(b.label);
            }
            return -1;
        }
        return a.label.localeCompare(b.label);
    }

    /**
     * @param {Object} params forwarded from a get request
     * @param {String} params.id
     * @returns {BehaviorSubject} a behavior subject according to the given params
     */
    function getSubject(params) {
        if (params?.id) {
            faceSubjects[params.id] ??= new BehaviorSubject();
            return faceSubjects[params.id];
        }
        faceSubjects[all] ??= new BehaviorSubject();
        return faceSubjects[all];
    }

    function applyRefreshParam(params) {
        if (params?.refresh) {
            if (params.id) {
                faceSubscriptionsForIcon?.[params.id]?.unsubscribe();
                delete faceSubscriptionsForIcon?.[params.id];
            } else {
                Object.keys(faceSubscriptionsForIcon).forEach(key => {
                    faceSubscriptionsForIcon?.[key]?.unsubscribe();
                    delete faceSubscriptionsForIcon?.[key];
                });
            }
        }
    }

    function clearFaceSubjects() {
        // no data, so all face subjects should be cleared with empty object or array
        // do not call .next(undefined) since that would mean no request was made
        Object.keys(faceSubjects).forEach(key => {
            if (key === all) {
                faceSubjects[key].next([]);
            } else {
                faceSubjects[key].next({});
            }
        });
    }

    function getData(params) {
        if (((params?.refresh) || !Object.keys(faceSubjects).some(key => faceSubjects[key].getValue()))
                && !getIdentitiesInProgress) {
            getIdentitiesInProgress = true;
            apiService.faces.getIdentities(params).then(
                identitiesResult => {
                    getIdentitiesInProgress = false;
                    if (Array.isArray(identitiesResult?.data?.items)) {
                        identitiesResult.data.items.sort(faceSort);

                        // clear single face subjects that were not in the results
                        Object.keys(faceSubjects).forEach(key => {
                            if (!identitiesResult.data.items.find(i => i.id === key) && key !== all) {
                                getSubject({ id: key }).next();
                            }
                        });

                        // update single face subjects
                        identitiesResult.data.items.forEach(identity => {
                            getSubject(identity).next(identity);
                        });

                        // update all face subjects
                        getSubject().next(identitiesResult.data.items);
                    } else {
                        clearFaceSubjects();
                    }
                },
                e => {
                    getIdentitiesInProgress = false;
                    clearFaceSubjects();
                    log({
                        code: 'getIdentitiesFail',
                        object: { params: params },
                        error: e,
                        traceId: params?.traceId
                    });
                }
            );
        }
    }

    function getIconKey(iconParams) {
        return iconParams.id + '-' + iconParams.size;
    }

    /**
     * @param {Identity} identity
     * @returns {Identity} An Identity object with PII and unnessessary data removed.
     */
    function identityForLog(identity) {
        return {
            id: identity?.id,
            primaryImageId: identity?.primaryImageId,
            imageIds: identity?.imageIds
        };
    }

    function paramsForLog(params) {
        const result = util.merge({}, params ?? {});
        delete result.traceId;
        return result;
    }

    function getFaceImageSuccess(arrayBuffer, subject, iconParams, identity) {
        util.arrayBufferToImage(arrayBuffer).subscribe({
            next: image => {
                subject.next(iconParams.renderFunction(iconParams, image));
            },
            error: e => {
                log({
                    code: 'primaryFaceToImage',
                    object: {
                        p: paramsForLog(iconParams),
                        i: identityForLog(identity)
                    },
                    error: e,
                    traceId: iconParams?.traceId
                });
                subject.next(faceUnknown);
            }
        });
    }

    function getBackupFaceImageSuccess(arrayBuffer, subject, iconParams, identity) {
        util.arrayBufferToImage(arrayBuffer).subscribe({
            next: image => {
                subject.next(iconParams.renderFunction(iconParams, image));
            },
            error: e => {
                log({
                    code: 'backupPrimaryFaceToImage',
                    object: {
                        p: paramsForLog(iconParams),
                        i: identityForLog(identity)
                    },
                    error: e,
                    traceId: iconParams?.traceId
                });
                subject.next(faceUnknown);
            }
        });
    }

    function getBackupFaceImageFail(error, subject, iconParams, identity) {
        log({
            code: 'getBackupPrimaryFaceImage',
            object: {
                p: paramsForLog(iconParams),
                i: identityForLog(identity)
            },
            error: error,
            traceId: iconParams?.traceId
        });
        subject.next(faceUnknown);
    }

    function getFaceImageFail(error, subject, iconParams, identity) {
        log({
            code: 'getPrimaryFaceImage',
            object: {
                p: paramsForLog(iconParams),
                i: identityForLog(identity)
            },
            error: error,
            traceId: iconParams?.traceId
        });

        // try to recover by using the backup legacy method
        apiService.events.getFaceImage({
            identityId: identity.id,
            imageId: identity.primaryImageId,
            traceId: iconParams?.traceId
        }).then(
            arrayBufferResult => getBackupFaceImageSuccess(arrayBufferResult.data, subject, iconParams, identity),
            error => getBackupFaceImageFail(error, subject, iconParams, identity)
        );
    }

    function getFaceImage(iconParams, subject, identity) {
        if (identity?.id && identity?.id.startsWith('-')) {
            subject.next(faceUnknown);
            return;
        }
        if (identity.none) {
            subject.next(faceNone);
            return;
        }
        if (identity.add) {
            subject.next(faceAdd);
            return;
        }

        apiService.events.getFacePrimaryImage({
            identityId: identity.id,
            traceId: iconParams?.traceId
        }).then(
            arrayBufferResult => getFaceImageSuccess(arrayBufferResult.data, subject, iconParams, identity),
            error => getFaceImageFail(error, subject, iconParams, identity)
        );
    }

    function updateAllFaceIconsForIdentity(identity, renderFunction) {
        Object.keys(faceIconSubjects).forEach(key => {
            if (identity?.id && key.startsWith(identity.id)) {
                // update this icon
                const params = key.split('-');
                const size = params[1];
                getFaceImage(
                    {
                        id: identity.id,
                        size: size,
                        renderFunction: renderFunction
                    },
                    faceIconSubjects[key],
                    identity
                );
            }
        });
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @param {Object} iconParams
     * @param {String} iconParams.id  The id of the identity to get a face icon for. Required.
     * @param {String|Number} iconParams.size The size in pixels of the icon. Required.
     * @param {Boolean} iconParams.refresh Truthy to get a fresh image.
     * @param {String} iconParams.traceId
     * @param {Function} iconParams.renderFunction
     * @returns {Observable<Canvas>|String} Emits a Canvas representing the face icon.
     *      The value may be converted to to a URL with .toDataURL().
     *      The Observable will emit any time the underlying identity changes.
     *      String values may be emitted for special cases such as faceUnknown, faceAdd and faceNone -
     *      the app may interpret these how it wants.
     * @name icon
     * @method
     */
    _faceService.icon = function(iconParams) {
        // input validation
        const internalParams = util.withTraceId(iconParams);
        internalParams.renderFunction = iconParams?.renderFunction;
        if (!internalParams.id
                || !internalParams.size
                || !internalParams.renderFunction) {
            return new Observable(subscriber => {
                const logConfig = {
                    code: 'icon',
                    descr: 'icon bad iconParams or bad options',
                    object: internalParams,
                    traceId: internalParams.traceId
                };
                log(logConfig);
                subscriber.error(logConfig);
            });
        }

        const key = getIconKey(internalParams);
        faceIconSubjects[key] ??= new BehaviorSubject(faceUnknown);

        // set a "watcher" subscription on the face
        // so that the associated icons will get updated every time a face is updated.
        // this only needs to be done once, even considering refresh
        if (!faceSubscriptionsForIcon[internalParams.id] || internalParams.refresh) {
            faceSubscriptionsForIcon?.[internalParams.id]?.unsubscribe();
            faceSubscriptionsForIcon[internalParams.id] = _faceService.get(internalParams).subscribe({
                next: identity => {
                    updateAllFaceIconsForIdentity(identity, internalParams.renderFunction);
                },
                error: () => {
                    Object.keys(faceIconSubjects).forEach(key => {
                        if (key.startsWith(internalParams.id)) {
                            faceIconSubjects[key].next(faceUnknown);
                        }
                    });
                }
            });
        } else {
            // If there is already a faceSubscriptionsForIcon, all that is needed is
            // to get the face and make the icon
            _faceService.get(internalParams).pipe(filter(f => f !== undefined), take(1)).subscribe({
                next: identity => {
                    getFaceImage(internalParams, faceIconSubjects[key], identity);
                }
            });
        }

        return faceIconSubjects[key].asObservable();
    };

    /**
     * @description Main method for retrieving face identities
     * @param {Object} [params] specify how and what to get
     * @param {String} [params.id] set to a faceId to request a single face
     * @param {Boolean} [params.refresh] reload data from the server
     * @param {Boolean} [params.traceId]
     * @returns {Observable<Face>} subscribe to get updates on a face or faces
     * @name get
     * @method
     */
    _faceService.get = function(params) {
        const paramsWithTraceId = util.withTraceId(params);
        applyRefreshParam(paramsWithTraceId);
        const subject = getSubject(paramsWithTraceId);
        getData(paramsWithTraceId);
        return subject.asObservable();
    };

    /**
     * @param {String} params
     * @param {String} params.label A label for the new identity
     * @returns {Observable<Identity>} Emits a new identity after it has been created on the server
     * @name addIdentity
     * @method
     */
    _faceService.addIdentity = function(params) {
        if (typeof params?.label !== 'string' || params?.label?.length === 0) {
            return new Observable(subscriber => {
                subscriber.error(
                    log({
                        code: 'addIdentityBadParams',
                        object: params?.label,
                        traceId: params?.traceId
                    })
                );
            });
        }
        return new Observable(subscriber => {
            const internalParams = util.withTraceId(params);
            apiService.faces.addIdentity(internalParams).then(
                addResult => {
                    if (addResult?.data?.id) {
                        _faceService.get({ refresh: true, traceId: internalParams.traceId }).pipe(take(1)).subscribe();

                        // inform the subscriber that the new identity is ready
                        subscriber.next(addResult.data);
                        subscriber.complete();
                    }
                },
                e => subscriber.error(
                    log({
                        code: 'addIdentityFail',
                        descr: 'add identity failed',
                        object: internalParams.label,
                        traceId: internalParams.traceId,
                        error: e
                    })
                )
            );
        });
    };

    // Any time loggedIn emits false, run clear
    apiService.loggedIn().pipe(filter(l => !l)).subscribe(() => clear());

    return Object.freeze(_faceService);
}

export default createFaceService;
